//#region header */
/**************************************************************************************************
//
//  Description: Form to display the organisation information
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   25.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Changed required for the V2 of the API.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

// import React, { useEffect, useRef, useState } from "react";
import React, { useEffect, useState } from "react";

import useUserQueryStore from "../context/UserStore";

import { OrganisationErrorType } from "../models/organisationErrorType";

import { Box, Grid2 } from "@mui/material";
import ADSTextControl from "../components/ADSTextControl";

import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";
import OrganisationGridType from "../models/organisationGridType";

interface OrganisationFormProps {
  data: OrganisationGridType;
  errors: OrganisationErrorType[] | undefined;
  onDataChange: (data: OrganisationGridType) => void;
}

const OrganisationForm: React.FC<OrganisationFormProps> = ({ data, errors, onDataChange }) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const [name, setName] = useState<string | undefined>();

  const [nameError, setNameError] = useState<string[] | undefined>();

  /**
   * Method to update the data when a field is changed.
   *
   * @param fieldName The name of the field that has been updated.
   * @param value The new value for the field.
   */
  const updateData = (fieldName: string, value: string | number | undefined): void => {
    const updatedOrganisation = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedOrganisation);
    }
  };

  /**
   * Event to handle when the name is changed.
   *
   * @param newValue The new name.
   */
  const handleNameChangeEvent = (newValue: string): void => {
    setName(newValue);
    updateData("organisation", newValue);
  };

  useEffect(() => {
    if (data) {
      setName(data.organisation);
    }
  }, [data]);

  useEffect(() => {
    setNameError(undefined);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        if (error.id === data.id) {
          switch (error.field) {
            case "name":
              setNameError([error.error]);
              break;

            default:
              break;
          }
        }
      }
    }
  }, [errors, data]);

  return (
    <Box sx={dataFormStyle("OrganisationForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
        <Grid2 size={12}>
          <ADSTextControl
            label="Name"
            value={name}
            maxLength={100}
            id="organisation_name"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The name of the organisation."
            errorText={nameError}
            onChange={handleNameChangeEvent}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default OrganisationForm;
